import React, { useEffect, useState } from "react";
import ReactTable from "react-table";
import { Link } from "react-router";
import Immutable from "immutable";
import Dialog from "@material-ui/core/Dialog";
import { capitalizeFirstLetter } from "/src/util/utilities";
import Button from "../Button"
import ContractReview from "../MemberCSMeter/ContractReview";

const VIEW_NAME = "Contract Review";
const QUEUE_STATUS = "contract_review";

const ContractReviewQueue = ({
  csEnrollments,
  fetchCSEnrollments,
  selectedView,
  adminUser,
  approval,
  doFetchGetDetails,
  doFetchMemberEnrollment,
  doRefreshMemberProduct,
  doFetchRequestTdsps,
  doCSMeterBodyUpdate,
}) => {
  const [page, setPage] = useState(1);
  const [reviewRow, setReviewRow] = useState(null);

  useEffect(() => {
    if (selectedView === VIEW_NAME) {
      setPage(1);
      fetchCSEnrollments(QUEUE_STATUS, 1);
      // the review modal needs the TDSP list for utility/meter-id config
      doFetchRequestTdsps();
    }
  }, [selectedView, fetchCSEnrollments]);

  const isCSMeterManager =
    adminUser && adminUser.permissions && adminUser.permissions.isCSMeterManager;

  const openReview = (row) => {
    // load everything the modal reads: the cs_meter (-> approval), the
    // enrollment and the member products (-> state.selectedMember)
    doFetchGetDetails(row.member_id);
    doFetchMemberEnrollment(row.member_id);
    doRefreshMemberProduct(row.member_id);
    setReviewRow(row);
  };

  const closeReview = () => {
    setReviewRow(null);
    // refresh the queue so acted-on records leave the list
    setPage(1);
    fetchCSEnrollments(QUEUE_STATUS, 1);
  };

  const columns = [
    {
      id: 'member_id',
      Header: 'Member ID',
      accessor: (row) => {
        if (row.member_id) {
          return <Link to={`/members/${row.member_id}/details`}>{row.member_id}</Link>
        }
        return row.member_id
      },
    },
    {
      Header: 'Name',
      accessor: 'name',
      Cell: ({ value }) => (<div style={{ textAlign: 'center' }}>{value}</div>)
    },
    {
      Header: 'Email',
      accessor: 'email',
      Cell: ({ value }) => (<div style={{ textAlign: 'center' }}>{value}</div>)
    },
    {
      Header: 'Phone',
      accessor: 'phone',
      Cell: ({ value }) => (<div style={{ textAlign: 'center' }}>{value}</div>)
    },
    {
      Header: 'Utility Name',
      accessor: 'utility_name',
      Cell: ({ value }) => (<div style={{ textAlign: 'center' }}>{value}</div>)
    },
    {
      Header: 'State Code',
      accessor: 'state',
      Cell: ({ value }) => (<div style={{ textAlign: 'center' }}>{value}</div>)
    },
    {
      Header: 'Meter Status',
      accessor: 'meter_status',
      Cell: ({ value }) => (<div style={{ textAlign: 'center' }}>{capitalizeFirstLetter(value)}</div>)
    },
    {
      Header: 'Status Reason',
      accessor: 'status_reason',
      Cell: ({ value }) => (<div style={{ textAlign: 'center' }}>{value}</div>)
    },
    {
      Header: 'Signing Status',
      accessor: 'document_signing_status',
      Cell: ({ value }) => (<div style={{ textAlign: 'center' }}>{value}</div>)
    },
    {
      Header: 'Contract Review Status',
      accessor: 'contract_review_status',
      Cell: ({ value }) => (<div style={{ textAlign: 'center' }}>{capitalizeFirstLetter(value)}</div>)
    },
    {
      id: 'enrollment_completion_date',
      Header: 'Enrollment Complete Date',
      accessor: (row) => String(row.enrollment_completion_date || '').slice(0, 10),
      Cell: ({ value }) => (<div style={{ textAlign: 'center' }}>{value}</div>)
    },
    {
      id: 'review',
      Header: '',
      sortable: false,
      filterable: false,
      accessor: (row) => row,
      Cell: ({ value: row }) => (
        isCSMeterManager ? (
          <div style={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => openReview(row)}
            >
              Review
            </Button>
          </div>
        ) : null
      ),
    },
  ];

  const handlePagination = () => {
    if (csEnrollments?.has_next) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchCSEnrollments(QUEUE_STATUS, nextPage);
    }
  };

  // only hand the meter to the modal once the details fetch for the clicked
  // row has landed — approval may still hold a previously viewed meter
  const meterLoaded =
    reviewRow && approval && approval.member_id === reviewRow.member_id;

  return (
    <div>
      <ReactTable
        data={csEnrollments?.list || []}
        columns={columns}
        className="-striped -highlight"
        defaultPageSize={10}
        defaultSorted={[{ id: 'enrollment_completion_date', desc: true }]}
        filterable
      />
      {csEnrollments?.has_next && (
        <Button
          variant="contained"
          color="primary"
          style={{
            marginTop: "20px",
            width: "150px",
            alignSelf: "flex-end",
          }}
          onClick={handlePagination}
        >
          Load More
        </Button>
      )}
      {(csEnrollments?.list || []).length > 0 && page * (csEnrollments?.limit || 100) < csEnrollments?.total ? (
        <div style={{ float: "right", marginTop: "10px" }}>
          Downloaded {page * (csEnrollments?.limit || 100)} of {csEnrollments?.total} contract reviews
        </div>
      ) : (
        (csEnrollments?.list || []).length > 0 && (
          <div style={{ float: "right", marginTop: "10px" }}>
            Downloaded all {csEnrollments?.total} contract reviews
          </div>
        )
      )}
      <Dialog
        onClose={closeReview}
        aria-labelledby="customized-dialog-title"
        open={!!meterLoaded}
      >
        {meterLoaded && (
          <ContractReview
            meter={Immutable.fromJS(approval)}
            memberID={reviewRow.member_id}
            closePopup={closeReview}
            handleChange={body =>
              doCSMeterBodyUpdate(approval.id, body, reviewRow.member_id)
            }
          />
        )}
      </Dialog>
    </div>
  );
};

export default ContractReviewQueue;
